import type { RunDetail, RunEvent } from '@/entities/sender.entity';
import {
	deriveRunPipelineView,
	isRunTerminalStatus,
	type PipelineHero,
	type PipelineStep,
	type PipelineStepState,
} from '@/entities/sender-run-pipeline';
import { usePipelineClock } from '@/hooks/use-pipeline-clock';
import { cn } from '@/lib/utils';
import {
	AlertCircle,
	CheckIcon,
	ChevronRightIcon,
	CircleIcon,
	Loader2Icon,
	MinusIcon,
} from 'lucide-react';
import { useMemo } from 'react';

interface RunPipelineCardProps {
	run: RunDetail;
	events: RunEvent[];
}

const HERO_VARIANT_CLASSES: Record<PipelineHero['variant'], string> = {
	active: 'border-chart-4/30 bg-gradient-to-br from-chart-4/10 to-transparent',
	success: 'border-chart-2/30 bg-gradient-to-br from-chart-2/10 to-transparent',
	warning: 'border-chart-4/40 bg-gradient-to-br from-chart-4/15 to-transparent',
	error: 'border-destructive/30 bg-gradient-to-br from-destructive/10 to-transparent',
	muted: 'border-border bg-muted/20',
};

const STEP_STATE_STYLES: Record<
	PipelineStepState,
	{ dot: string; text: string; icon: typeof CheckIcon }
> = {
	completed: {
		dot: 'bg-chart-2',
		text: 'text-chart-2',
		icon: CheckIcon,
	},
	active: {
		dot: 'bg-chart-4',
		text: 'text-chart-4',
		icon: Loader2Icon,
	},
	pending: {
		dot: 'bg-muted-foreground/30',
		text: 'text-muted-foreground',
		icon: CircleIcon,
	},
	skipped: {
		dot: 'bg-muted-foreground/20',
		text: 'text-muted-foreground',
		icon: MinusIcon,
	},
	failed: {
		dot: 'bg-destructive',
		text: 'text-destructive',
		icon: AlertCircle,
	},
	cancelled: {
		dot: 'bg-muted-foreground/40',
		text: 'text-muted-foreground',
		icon: MinusIcon,
	},
};

export default function RunPipelineCard({ run, events }: RunPipelineCardProps) {
	const isRunActive = !isRunTerminalStatus(run.status);
	const now = usePipelineClock(isRunActive);
	const view = useMemo(() => deriveRunPipelineView(run, events, now), [run, events, now]);

	const progressPercent =
		view.hero.progress != null && view.hero.progress.max > 0
			? Math.min(100, Math.round((view.hero.progress.value / view.hero.progress.max) * 100))
			: null;

	return (
		<div className='flex flex-col gap-3'>
			<div className={cn('rounded-xl border-2 px-3 py-3', HERO_VARIANT_CLASSES[view.hero.variant])}>
				<p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
					Сейчас
				</p>
				<p className='mt-1 text-base font-semibold leading-snug'>{view.hero.title}</p>
				{view.hero.subtitle ? (
					<p className='mt-0.5 text-sm text-muted-foreground'>{view.hero.subtitle}</p>
				) : null}
				{view.hero.eta ? (
					<p className='mt-2 text-sm font-medium tabular-nums'>{view.hero.eta}</p>
				) : null}
				{progressPercent != null && view.hero.progress ? (
					<div className='mt-2 flex flex-col gap-1'>
						<div className='h-1.5 rounded-full bg-muted overflow-hidden'>
							<div
								className={cn(
									'h-full rounded-full transition-all',
									view.hero.variant === 'success' ? 'bg-chart-2' : 'bg-chart-4',
								)}
								style={{ width: `${progressPercent}%` }}
							/>
						</div>
						<p className='text-xs text-muted-foreground tabular-nums'>
							{view.hero.progress.value} / {view.hero.progress.max} ({progressPercent}%)
						</p>
					</div>
				) : null}
			</div>

			{view.nextSteps.length > 0 ? (
				<div className='rounded-lg border border-border bg-muted/20 px-3 py-2.5'>
					<p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground'>
						Дальше
					</p>
					<ul className='mt-2 flex flex-col gap-1.5'>
						{view.nextSteps.map((step, index) => (
							<li key={`${step.label}-${index}`} className='flex items-start gap-2 text-sm'>
								<ChevronRightIcon className='size-4 shrink-0 text-muted-foreground mt-0.5' />
								<span>{step.label}</span>
							</li>
						))}
					</ul>
				</div>
			) : null}

			<div className='rounded-lg border border-border bg-muted/10 px-3 py-2 max-h-60 overflow-y-auto'>
				<p className='text-[10px] font-semibold uppercase tracking-wider text-muted-foreground mb-2'>
					Этапы
				</p>
				<div className='flex flex-col'>
					{view.steps.map((step, index) => (
						<PipelineStepRow key={step.id} step={step} isLast={index === view.steps.length - 1} />
					))}
				</div>
			</div>
		</div>
	);
}

function PipelineStepRow({ step, isLast }: { step: PipelineStep; isLast: boolean }) {
	const styles = STEP_STATE_STYLES[step.state];
	const Icon = styles.icon;
	const isActive = step.state === 'active';

	return (
		<div className='flex gap-3'>
			<div className='flex flex-col items-center shrink-0 pt-0.5'>
				<div
					className={cn(
						'flex size-5 items-center justify-center rounded-full',
						step.state === 'active' ? 'bg-chart-4/15 animate-pulse' : 'bg-muted/40',
					)}
				>
					<Icon
						className={cn('size-3', styles.text, isActive && 'animate-spin')}
					/>
				</div>
				{!isLast ? <div className='w-px flex-1 min-h-3 bg-border mt-1' /> : null}
			</div>

			<div className={cn('flex-1 min-w-0 pb-2', isLast && 'pb-0')}>
				<div className='flex items-center justify-between gap-2'>
					<span
						className={cn(
							'text-sm',
							step.state === 'active' && 'font-medium',
							step.state === 'pending' && 'text-muted-foreground',
							step.state === 'skipped' && 'text-muted-foreground line-through decoration-muted-foreground/50',
							step.state === 'failed' && 'text-destructive font-medium',
							step.state === 'cancelled' && 'text-muted-foreground font-medium',
						)}
					>
						{step.label}
					</span>
					{step.durationLabel ? (
						<span className='text-xs text-muted-foreground tabular-nums shrink-0'>
							{step.durationLabel}
						</span>
					) : step.state === 'skipped' ? (
						<span className='text-xs text-muted-foreground shrink-0'>пропущено</span>
					) : step.state === 'cancelled' ? (
						<span className='text-xs text-muted-foreground shrink-0'>отменён</span>
					) : null}
				</div>
			</div>
		</div>
	);
}
